/**
 * Utility functions for displaying lifestyle questions and answers in user-friendly format
 * Uses i18n translations to support English/Vietnamese display
 */

import { getLifestyleCategoriesI18n, getTranslatedOptionLabel } from './lifestyleCategoriesI18n';
import { lifestyleCategories } from './lifestyleCategories';

/**
 * Extract category and parameter key from standardized parameter name
 * @param {string} parameterKey - Standardized parameter key (e.g., 'lifestyle_nutrition_protein_intake')
 * @returns {Object} - { category, paramKey } or null if not a lifestyle parameter
 */
export const parseLifestyleParameterKey = (parameterKey) => {
  if (!parameterKey || typeof parameterKey !== 'string') {
    return null;
  }

  // Handle both standardized format (lifestyle_category_param) and legacy format (Lifestyle_param)
  const standardizedMatch = parameterKey.match(/^lifestyle_([^_]+)_(.+)$/);
  if (standardizedMatch) {
    return {
      category: standardizedMatch[1],
      paramKey: standardizedMatch[2]
    };
  }

  // Legacy format fallback
  const legacyMatch = parameterKey.match(/^Lifestyle_(.+)$/);
  if (legacyMatch) {
    // Try to find the parameter in the categories to determine category
    for (const cat of lifestyleCategories) {
      const param = cat.parameters.find(p => p.key === legacyMatch[1]);
      if (param) {
        return {
          category: cat.key,
          paramKey: legacyMatch[1]
        };
      }
    }
  }

  return null;
};

/**
 * Get the question text for a lifestyle parameter
 * @param {string} parameterKey - Standardized parameter key
 * @param {Function} t - i18n translation function
 * @returns {string} - Question text in current language
 */
export const getLifestyleQuestionText = (parameterKey, t) => {
  const parsed = parseLifestyleParameterKey(parameterKey);
  if (!parsed) {
    return null;
  }

  const { category, paramKey } = parsed;

  // Get i18n-enabled categories
  const categories = getLifestyleCategoriesI18n(t);
  const categoryObj = categories.find(cat => cat.key === category);
  
  if (!categoryObj) {
    return null;
  }

  // Find the parameter
  const parameter = categoryObj.parameters.find(p => p.key === paramKey);
  
  if (!parameter) {
    return null;
  }

  return parameter.questionText || parameter.name;
};

/**
 * Get the human-readable answer text for a lifestyle parameter value
 * @param {string} parameterKey - Standardized parameter key
 * @param {any} value - Raw parameter value
 * @param {Function} t - i18n translation function
 * @returns {string} - Human-readable answer text in current language
 */
export const getLifestyleAnswerText = (parameterKey, value, t) => {
  const parsed = parseLifestyleParameterKey(parameterKey);
  if (!parsed) {
    return String(value);
  }

  const { category, paramKey } = parsed;

  // Get i18n-enabled categories
  const categories = getLifestyleCategoriesI18n(t);
  const categoryObj = categories.find(cat => cat.key === category);
  
  if (!categoryObj) {
    return String(value);
  }

  // Find the parameter
  const parameter = categoryObj.parameters.find(p => p.key === paramKey);
  
  if (!parameter) {
    return String(value);
  }

  // Handle different parameter types
  if (parameter.type === 'BooleanCheckbox' || parameter.type === 'BooleanCheckboxOption') {
    // For boolean checkboxes, return Yes/No or translated equivalent
    if (value === true) {
      return t('common.yes', { defaultValue: 'Yes' });
    } else if (value === false) {
      return t('common.no', { defaultValue: 'No' });
    }
    return '-';
  }

  if (parameter.type === 'Questionnaire' && parameter.options) {
    // Find the matching option
    const option = parameter.options.find(opt => opt.value === value || String(opt.value) === String(value));
    
    if (option) {
      // Use the translated label
      return getTranslatedOptionLabel(option, t);
    }
  }

  // Fallback to raw value
  return String(value);
};

/**
 * Get the category title for a lifestyle parameter
 * @param {string} parameterKey - Standardized parameter key
 * @param {Function} t - i18n translation function
 * @returns {string} - Category title in current language
 */
export const getLifestyleCategoryTitle = (parameterKey, t) => {
  const parsed = parseLifestyleParameterKey(parameterKey);
  if (!parsed) {
    return null;
  }

  const { category } = parsed;

  // Get i18n-enabled categories
  const categories = getLifestyleCategoriesI18n(t);
  const categoryObj = categories.find(cat => cat.key === category);
  
  return categoryObj ? categoryObj.title : null;
};

/**
 * Get complete lifestyle parameter display information
 * @param {string} parameterKey - Standardized parameter key
 * @param {any} value - Raw parameter value
 * @param {string} timestamp - ISO timestamp string
 * @param {Function} t - i18n translation function
 * @returns {Object} - Complete display information
 */
export const getLifestyleParameterDisplayInfo = (parameterKey, value, timestamp, t) => {
  return {
    category: getLifestyleCategoryTitle(parameterKey, t),
    question: getLifestyleQuestionText(parameterKey, t),
    answer: getLifestyleAnswerText(parameterKey, value, t),
    rawValue: value,
    timestamp: timestamp,
    parameterKey: parameterKey
  };
};

/**
 * Check if a parameter is a lifestyle parameter
 * @param {string} parameterKey - Parameter key to check
 * @returns {boolean} - True if it's a lifestyle parameter
 */
export const isLifestyleParameterKey = (parameterKey) => {
  if (!parameterKey || typeof parameterKey !== 'string') {
    return false;
  }
  
  return parameterKey.startsWith('lifestyle_') || parameterKey.startsWith('Lifestyle_');
};

